import axios from "axios";

const CancelTokenAPI = axios.CancelToken;
export let source = CancelTokenAPI.source();
let cancelToken = {cancelToken: source.token}

const RESOURCES = {
  SERVER: "/metatrader-servers",
  APPLICATION_INSTANCE: "/application-instances",
  APPLICATION_DEFINITION: "/application-definitions",
  APPLICATION: "/applications",
  EVENT_RULE: "/eventrules",
  APPLICATION_CONFIGURATION: "/application-configurations",
  AUTHENTICATION: {
    REFRESH: "/auth/refresh",
    LOGIN: "/auth/login",
    LOGOUT: "/auth/logout",
  },
  USER: {
    DEFAULT: "/users",
    ME: "/users/me",
    PASSWORDUPDATE: "/users/passwordUpdate",
  },
  TRADE_WATCHER: {
    ALERT_TYPES: "/alert-types",
    ALERT_CARDS: "/alert-cards",
    ALERTS: "/alerts",
    ALERT_STATISTICS: "/alert-statistics",
  },
  DEBUGGER: {
    APPLICATION_EVENT_HANDLER: {
      DEFAULT: "/debug/application-event-handler",
      RESET_METRICS: "/debug/application-event-handler/reset-metrics",
    },
    STATISTICS: {
      PRICEGUARD: "/debug/statistics/priceguard"
    },
    SCHEDULER: "/debug/scheduler"
  },
  NOTIFICATIONS: {
    SUBSCRIPTIONGROUP: "/eventnotifier/subscription_group",
    CHANNELTYPES: "/eventnotifier/channel_type",
    TENANTS: "/eventnotifier/tenants",
    CHANNELS: "/eventnotifier/channels",
    CHANNELTYPE: "/eventnotifier/channel_type",
    CHANNELTYPETENANT: "/eventnotifier/channel_type_tenant"
  },
  CHARTS: "/charts",
  QUERY: {
    CHART: "/chart-query"
  },
};

const ApiService = {
  query(resource, params) {
    return axios.get(resource, {...params, ...cancelToken});
  },

  get(resource, slug = "") {
    return axios.get(`${resource}/${slug}`, cancelToken);
  },

  post(resource, params = {}, configs = {}) {
    return axios.post(`${resource}`, params, {
      withCredentials: true,
      SameSite: "none",
      ...cancelToken,
      ...configs
    });
  },

  update(resource, slug, params) {
    return axios.put(`${resource}/${slug}`, params, cancelToken);
  },

  put(resource, params) {
    return axios.put(`${resource}`, params, cancelToken);
  },

  patch(resource, params) {
    return axios.patch(`${resource}`, params, cancelToken);
  },

  delete(resource) {
    return axios.delete(resource, cancelToken);
  },
};

export default ApiService;

export const ServerService = {
  get(slug) {
    return ApiService.get(RESOURCES.SERVER, slug);
  },
  post(params) {
    return ApiService.post(RESOURCES.SERVER, params);
  },
  put(slug, params) {
    return ApiService.put(`${RESOURCES.SERVER}/${slug}`, params);
  },
  query(params) {
    return ApiService.query(RESOURCES.SERVER, params);
  },
  delete(slug) {
    return ApiService.delete(`${RESOURCES.SERVER}/${slug}`);
  },
};

export const ApplicationInstanceService = {
  get(slug) {
    return ApiService.get(RESOURCES.APPLICATION_INSTANCE, slug);
  },

  query(params) {
    return ApiService.query(RESOURCES.APPLICATION_INSTANCE, params);
  },

  post(params) {
    return ApiService.post(RESOURCES.APPLICATION_INSTANCE, params);
  },

  update(slug, params) {
    return ApiService.update(RESOURCES.APPLICATION_INSTANCE, slug, params);
  },

  delete(slug) {
    return ApiService.delete(`${RESOURCES.APPLICATION_INSTANCE}/${slug}`);
  },

  put(slug, params) {
    return ApiService.put(`${RESOURCES.APPLICATION_INSTANCE}/${slug}`, params);
  },

  setRecoverySettings(instanceId, command = 'CMD_AN_RECOVER_DAILY_PROFITS', payload) {
    const payloadData = {
      command: command,
      payload: payload,
    };
    return ApiService.put(`${RESOURCES.APPLICATION_INSTANCE}/${instanceId}`, payloadData);
  },
};

export const ApplicationDefinitionService = {
  get(slug) {
    return ApiService.get(RESOURCES.APPLICATION_DEFINITION, slug);
  },
  post(params) {
    return ApiService.post(RESOURCES.APPLICATION_DEFINITION, params);
  },
  query(params) {
    return ApiService.query(RESOURCES.APPLICATION_DEFINITION, params);
  },
  delete(slug) {
    return ApiService.delete(`${RESOURCES.APPLICATION_DEFINITION}/${slug}`);
  },
  update(slug, params) {
    return ApiService.update(RESOURCES.APPLICATION_DEFINITION, slug, params);
  },
  patch(params) {
    return ApiService.patch(RESOURCES.APPLICATION_DEFINITION, params);
  },
};

export const ApplicationService = {
  get(slug) {
    return ApiService.get(RESOURCES.APPLICATION, slug);
  },
  post(params) {
    return ApiService.post(RESOURCES.APPLICATION, params);
  },
  delete(slug) {
    return ApiService.delete(RESOURCES.APPLICATION, slug);
  },
};

export const EventRuleService = {
  get(slug) {
    return ApiService.get(RESOURCES.EVENT_RULE, slug);
  },
  query(params) {
    return ApiService.query(RESOURCES.EVENT_RULE, params);
  },
  post(params) {
    return ApiService.post(RESOURCES.EVENT_RULE, params);
  },
  put(slug, params) {
    return ApiService.put(
        `${RESOURCES.EVENT_RULE}/${slug}?reloadRules=true`,
        params
    );
  },
  delete(slug) {
    return ApiService.delete(`${RESOURCES.EVENT_RULE}/${slug}`);
  },
};

export const SettingsService = {
  get(slug) {
    return ApiService.get(RESOURCES.APPLICATION_CONFIGURATION, slug);
  },
  getMe(params) {
    return ApiService.query(`${RESOURCES.APPLICATION_CONFIGURATION}/me`, params);
  },
  post(params) {
    return ApiService.post(RESOURCES.APPLICATION_CONFIGURATION, params);
  },
  put(slug, params) {
    return ApiService.put(
        `${RESOURCES.APPLICATION_CONFIGURATION}/${slug}`,
        params
    );
  },
  delete(slug) {
    return ApiService.delete(`${RESOURCES.APPLICATION_CONFIGURATION}/${slug}`);
  },
};

export const AuthenticationService = {
  login(params) {
    return ApiService.post(RESOURCES.AUTHENTICATION.LOGIN, params, {timeout: 20000});
  },
  logout(params) {
    return ApiService.post(RESOURCES.AUTHENTICATION.LOGOUT, params);
  },
  refresh() {
    return ApiService.post(RESOURCES.AUTHENTICATION.REFRESH, {}, {timeout: 15000});
  },
};

export const UserService = {
  get(slug) {
    return ApiService.get(RESOURCES.USER.DEFAULT, slug);
  },
  post(params) {
    return ApiService.post(RESOURCES.USER.DEFAULT, params);
  },
  put(slug, params) {
    return ApiService.put(
        `${RESOURCES.USER.DEFAULT}/${slug}`,
        params
    );
  },
  delete(slug) {
    return ApiService.delete(`${RESOURCES.USER.DEFAULT}/${slug}`);
  },
  getUserInfo() {
    return ApiService.get(RESOURCES.USER.ME);
  },
  updatePassword(params) {
    return ApiService.put(
        RESOURCES.USER.PASSWORDUPDATE,
        params,
    );
  }
};

export const TradeWatcherService = {
  AlertTypes: {
    get(slug) {
      return ApiService.get(RESOURCES.TRADE_WATCHER.ALERT_TYPES, slug);
    },
    post(params) {
      return ApiService.post(RESOURCES.TRADE_WATCHER.ALERT_TYPES, params);
    },
    delete(slug) {
      return ApiService.delete(`${RESOURCES.TRADE_WATCHER.ALERT_TYPES}/${slug}`);
    },
    put(slug, params) {
      return ApiService.put(`${RESOURCES.TRADE_WATCHER.ALERT_TYPES}/${slug}`, params);
    }
  },
  AlertCards: {
    get() {
      return ApiService.get(RESOURCES.TRADE_WATCHER.ALERT_CARDS);
    },
    getById(slug = "") {
      return ApiService.get(RESOURCES.TRADE_WATCHER.ALERT_CARDS, slug);
    },
    post(params) {
      return ApiService.post(RESOURCES.TRADE_WATCHER.ALERT_CARDS, params);
    },
    exportFile(params) {
      return ApiService.post(`${RESOURCES.TRADE_WATCHER.ALERT_CARDS}/exportFile`, params);
    },
    importFile(params) {
      const reqBody = params.fileString;
      const overwriteTypes = params.overwriteTypes;
      const overwriteCards = params.overwriteCards;
      return ApiService.post(`${RESOURCES.TRADE_WATCHER.ALERT_CARDS}/import?overwriteType=${overwriteTypes}&overwriteCard=${overwriteCards}`, reqBody);
    },
    put(slug, params) {
      return ApiService.put(`${RESOURCES.TRADE_WATCHER.ALERT_CARDS}/${slug}`, params);
    },
    delete(slug) {
      return ApiService.delete(`${RESOURCES.TRADE_WATCHER.ALERT_CARDS}/${slug}`);
    },
  },
  AlertStatistics: {
    query(params) {
      return ApiService.query(RESOURCES.TRADE_WATCHER.ALERT_STATISTICS, params);
    },
  },
  Alerts: {
    get(slug = "") {
      return ApiService.query(`${RESOURCES.TRADE_WATCHER.ALERTS}/{id}`, {params: {id: slug}});
    },
    query(params) {
      return ApiService.query(RESOURCES.TRADE_WATCHER.ALERTS, params);
    },
    patch(params) {
      return ApiService.patch(`${RESOURCES.TRADE_WATCHER.ALERTS}/status`, params);
    },
    delete(slug = "") {
      return ApiService.delete(`${RESOURCES.TRADE_WATCHER.ALERTS}/${slug}`);
    },
    deleteByCardId(slug = "") {
      return ApiService.delete(`${RESOURCES.TRADE_WATCHER.ALERTS}/alert-card/${slug}`);
    },
  }
};
export const DebugService = {
  ApplicationEventHandler: {
    get(slug = "") {
      return ApiService.get(RESOURCES.DEBUGGER.APPLICATION_EVENT_HANDLER.DEFAULT, slug);
    },
    reset(params = {}) {
      return ApiService.post(RESOURCES.DEBUGGER.APPLICATION_EVENT_HANDLER.RESET_METRICS, params);
    },
    post(slug) {
      return ApiService.post(`${RESOURCES.DEBUGGER.APPLICATION_EVENT_HANDLER.DEFAULT}/${slug}`);
    }
  },
  Statistics: {
    Priceguard: {
      get(slug = "") {
        return ApiService.get(RESOURCES.DEBUGGER.STATISTICS.PRICEGUARD, slug);
      }
    }
  },
  Scheduler: {
    post(params) {
      return ApiService.post(RESOURCES.DEBUGGER.SCHEDULER, params);
    }
  }
}

export const ChartsService = {
  get(slug) {
    return ApiService.get(RESOURCES.CHARTS, slug);
  },
  post(params) {
    return ApiService.post(RESOURCES.CHARTS, params);
  },
  delete(slug) {
    return ApiService.delete(`${RESOURCES.CHARTS}/${slug}`);
  },
  put(slug, params) {
    return ApiService.put(`${RESOURCES.CHARTS}/${slug}`, params);
  },
};

export const NotificationService = {
  NotificationGroups: {
    get() {
      return ApiService.get(RESOURCES.NOTIFICATIONS.SUBSCRIPTIONGROUP);
    },
    getById(groupId) {
      return ApiService.get(`${RESOURCES.NOTIFICATIONS.SUBSCRIPTIONGROUP}/${groupId}`);
    },
    post(data) {
      return ApiService.post(RESOURCES.NOTIFICATIONS.SUBSCRIPTIONGROUP, data);
    },
    put(data) {
      const subId = data.id;
      const requestBody = {
        name: data.name,
        description: data.description,
        active: data.active
      };
      return ApiService.put(`${RESOURCES.NOTIFICATIONS.SUBSCRIPTIONGROUP}/${subId}`, requestBody);
    },
    delete(groupId) {
      return ApiService.delete(`${RESOURCES.NOTIFICATIONS.SUBSCRIPTIONGROUP}/${groupId}`);
    }
  },
  EventRules: {
    linkSubscriptionGroup(data) {
      const subId = data.subId;
      const entityId = data.entityId;
      const entityType = data.entityType || 'PRICEGUARD';
      return ApiService.put(`${RESOURCES.NOTIFICATIONS.SUBSCRIPTIONGROUP}/notificationEvent?id=${subId}&entityId=${entityId}&entityType=${entityType}`);
    },
    unlinkSubscriptionGroup(data) {
      const subId = data.subId;
      const entityId = data.entityId;
      const entityType = data.entityType || 'PRICEGUARD';
      return ApiService.delete(`${RESOURCES.NOTIFICATIONS.SUBSCRIPTIONGROUP}/notificationEvent?id=${subId}&entityId=${entityId}&entityType=${entityType}`);
    },
    getLinkedGroups(data) {
      const entityId = data.entityId;
      const entityType = data.entityType || 'PRICEGUARD';
      return ApiService.get(`${RESOURCES.NOTIFICATIONS.SUBSCRIPTIONGROUP}/${entityType}/${entityId}`);
    }
  },
  AlertCards: {
    get(alertId) {
      return ApiService.get(`${RESOURCES.NOTIFICATIONS.SUBSCRIPTIONGROUP}/ALERTCARD/${alertId}`);
    },
    // Link a subscription group to an alert card
    put(data) {
      const subId = data.subId;
      const entityId = data.entityId;
      const entityType = data.entityType;
      return ApiService.put(`${RESOURCES.NOTIFICATIONS.SUBSCRIPTIONGROUP}/notificationEvent?id=${subId}&entityId=${entityId}&entityType=${entityType}`);
    },
    delete(data) {
      const subId = data.subId;
      const entityId = data.entityId;
      const entityType = data.entityType;
      return ApiService.delete(`${RESOURCES.NOTIFICATIONS.SUBSCRIPTIONGROUP}/notificationEvent?id=${subId}&entityId=${entityId}&entityType=${entityType}`);
    }
  },
  ChannelTypes: {
    get() {
      return ApiService.get(RESOURCES.NOTIFICATIONS.CHANNELTYPES);
    }
  },
  Tenants: {
    get() {
      return ApiService.get(RESOURCES.NOTIFICATIONS.TENANTS);
    },
    post(data) {
      return ApiService.post(RESOURCES.NOTIFICATIONS.TENANTS, data);
    },
    put(data) {
      return ApiService.put(RESOURCES.NOTIFICATIONS.TENANTS, data);
    },
    addChannel(tenantId, channelId) {
      const data = {tenant: tenantId, channelId: channelId};
      return ApiService.post(`${RESOURCES.NOTIFICATIONS.TENANTS}/addChannel`, data);
    }
  },
  Channels: {
    get() {
      return ApiService.get(RESOURCES.NOTIFICATIONS.CHANNELS);
    },
    post(data) {
      return ApiService.post(RESOURCES.NOTIFICATIONS.CHANNELS, data);
    },
    put(data) {
      return ApiService.put(RESOURCES.NOTIFICATIONS.CHANNELS, data);
    },
    deleteChannel(channelId) {
      return ApiService.delete(`${RESOURCES.NOTIFICATIONS.CHANNELS}/${channelId}`);
    },
    getTypes() {
      return ApiService.get(RESOURCES.NOTIFICATIONS.CHANNELTYPE);
    },
    getTenantChannels() {
      return ApiService.get(RESOURCES.NOTIFICATIONS.CHANNELTYPETENANT);
    },
    addChannelToGroup(groupId, channelId) {
      return ApiService.put(`${RESOURCES.NOTIFICATIONS.SUBSCRIPTIONGROUP}/${groupId}/channel/${channelId}`);
    },
    removeChannelFromGroup(groupId, channelId) {
      return ApiService.delete(`${RESOURCES.NOTIFICATIONS.SUBSCRIPTIONGROUP}/${groupId}/channel/${channelId}`);
    },
  }
}

export function refreshCancelToken() {
  source = null
  cancelToken = null
  source = CancelTokenAPI.source();
  cancelToken = {cancelToken: source.token}
}

export const QueryService = {
  Chart: {
    post(params) {
      return ApiService.post(RESOURCES.QUERY.CHART, params);
    }
  }
  // post(params) {
  //   return ApiService.post(RESOURCES.CHARTS, params);
  // },
  // delete(slug) {
  //   return ApiService.delete(`${RESOURCES.CHARTS}/${slug}`);
  // },
  // put(slug, params) {
  //   return ApiService.put(`${RESOURCES.CHARTS}/${slug}`, params);
  // },
};

/**
 * Update the settings for the current account on the database
 *
 * @param settings {Object} - the settings data to update
 * @param recursive {Boolean}
 */
export function updateAppSettingsRemotely({settings, recursive}) {
  let profileId = parseInt(
      localStorage.getItem("currentSettingsProfileId"),
      10
  );

  if (!profileId && profileId !== 0) {
    if (this.isAdmin)
      console.log(
          "No profile ID found when updating the application state."
      );
    return;
  }

  let requestStatus = "200";

  SettingsService.put(profileId, settings)
      .then(() => {
      })
      .catch((error) => {
        if (
            error.hasOwnProperty("message") &&
            error.message.search("404") !== -1 &&
            !recursive
        ) {
          requestStatus = "404";
          // Get the configuration for the current type of device
          return SettingsService.getMe({
            params: {
              device:
                  localStorage.getItem("selected-device-type") || "Desktop",
            },
          });
          // return SettingsService.getMe({ params: { device: "Desktop" } })
        }
      })
      .then((response) => {
        if (requestStatus === "404" && response.data && !recursive) {
          localStorage.setItem(
              "currentSettingsProfileId",
              response.data.id
          );

          this.updateAppSettingsRemotely({
            settings: settings,
            recursive: true,
          });
        }
      })
      .catch((error) => {
        if (
            error.hasOwnProperty("message") &&
            error.message.search("404") !== -1
        ) {
          this.postNewSetting(false, settings);
        }
      });
}
